import { clearDrafts } from "../utils/userStorage";
import { clearReturnTo } from "../utils/session";
import React, {
  createContext,
  useState,
  useCallback,
  useContext,
} from "react";

// Create the context
const UserContext = createContext();

// Initial state for user data
const INITIAL_STATE = {
  id: 0,
  roles: [],
  firstName: "",
  lastName: "",
  lastLoginDate: "",
  token: "",
};

// Create the UserProvider component
export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(() => {
    const user = sessionStorage.getItem("user");
    return JSON.parse(user) || INITIAL_STATE;
  });

  // Persisted synchronously here rather than in a useEffect keyed on userData:
  // setUser is immediately followed by a route navigation in the login flow, and
  // React fires the new route's child effects (e.g. the notification/client fetches
  // that read this token) before this provider's own effect would run, so an
  // effect-based write loses the race and those first requests go out with no token.
  const handleUser = (userProps) => {
    const { id, roles, firstName, lastName, lastLoginDate, token } = userProps;
    const nextUserData = { id, roles, firstName, lastName, lastLoginDate, token };

    sessionStorage.setItem("user", JSON.stringify(nextUserData));
    setUserData(nextUserData);
  };

  // reason "logout": the agent chose to sign out, so unsaved drafts (which can
  // hold client details) and the remembered page are deleted.
  // reason "expired": the session ran out; both are kept so the same agent can
  // sign back in and carry on (another agent signing in still clears drafts).
  const resetUser = useCallback(({ reason = "logout" } = {}) => {
    if (reason === "logout") {
      clearDrafts();
      clearReturnTo();
    }
    sessionStorage.clear();
    setUserData(INITIAL_STATE);
  }, []);

  const setUser = useCallback((userProps) => {
    handleUser(userProps);
  }, []);

  return (
    <UserContext.Provider value={{ ...userData, setUser, resetUser }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
